// add flow
import { getAuthToken, getBidderIdFromToken } from './user';
import api from '../api/bidLimit';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from 'reselect';
import difference from 'lodash/difference';
import { getCatalogRegistration } from './catalogRegistration';
import { getDeployment } from './config';
import { getLiveItemStatus } from './liveItemStatus';
import type { GlobalState } from '../rootReducer';
import isEmpty from 'lodash/isEmpty';
import ms from 'ms';
import union from 'lodash/union';

/* Action Types */
export const LOAD_BID_LIMIT_FAIL = 'la/domain/bidLimit/LOAD_FAIL';
export const LOAD_BID_LIMIT_REQUEST = 'la/domain/bidLimit/LOAD_REQUEST';
export const LOAD_BID_LIMIT_SUCCESS = 'la/domain/bidLimit/LOAD_SUCCESS';

const REDUX_STORE_TIME = ms('1s');

/* reducer */
const DEFAULT_STATE = {
    byId: {},
    loaded: {},
    loading: [],
};

export type State = {|
    ...typeof DEFAULT_STATE,
|};

export default function reducer(state: State = DEFAULT_STATE, action = {}) {
    let existing;
    let loaded;
    let loading;
    let time;

    switch (action.type) {
        case LOAD_BID_LIMIT_FAIL:
            return {
                ...state,
                loading: difference(state.loading, [action.meta.catalogId]),
            };
        case LOAD_BID_LIMIT_REQUEST:
            return {
                ...state,
                loading: union(state.loading, [action.payload]),
            };
        case LOAD_BID_LIMIT_SUCCESS:
            existing = cloneDeep(state.byId);
            loaded = { ...state.loaded };
            loading = cloneDeep(state.loading);
            time = action.meta.actionTime;

            existing[action.payload.catalogId] = { balance: action.payload.bidLimitRemaining };
            loaded[action.payload.catalogId] = time;
            loading = difference(loading, [action.payload.catalogId]);

            return {
                ...state,
                byId: existing,
                loaded,
                loading,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.bidLimit;
const idSelector = (state: GlobalState, id) => id;

const byIdSelector = createSelector(stateSelector, state => state.byId);

const loadedSelector = createSelector(stateSelector, state => state.loaded);

const loadingSelector = createSelector(stateSelector, state => state.loading);

export const getBidLimitBalance = (state, catalogId, itemId) => {
    const byId = byIdSelector(state);
    const catalogRegistration = getCatalogRegistration(state, catalogId);
    if (!catalogRegistration.limited) {
        return Infinity;
    }
    const { bidderLiveBid, open } = getLiveItemStatus(state, itemId);

    const data = byId[catalogId] || { balance: 0 };
    const serverBalance = data.balance || 0;

    const balance = open ? serverBalance - bidderLiveBid : serverBalance;
    return Math.max(balance, 0);
};

const getLoadTime = createSelector([loadedSelector, idSelector], (loaded, id) => loaded[id] || 0);

const isLoading = createSelector([loadingSelector, idSelector], (loading, id) => loading.includes(id));

const shouldFetch = (state, catalogId) => {
    const item = getBidLimitBalance(state, catalogId);
    const loaded = getLoadTime(state, catalogId);
    const loading = isLoading(state, catalogId);
    const authToken = getAuthToken(state);
    const bidderId = getBidderIdFromToken(state);

    if (!authToken) return false;
    if (bidderId === -1) return false;
    if (!catalogId) return false;
    if (!isEmpty(item)) {
        const time = Date.now();
        const diff = time - loaded;
        if (diff < REDUX_STORE_TIME) return false;
    }
    return !loading;
};

/* ACTION CREATORS */
export const fetchBidLimitBalanceIfNeeded = catalogId => async (dispatch, getState) => {
    const state = getState();
    if (shouldFetch(state, catalogId)) {
        const authToken = getAuthToken(state);
        const bidderId = getBidderIdFromToken(state);
        const deployment = getDeployment(state);
        try {
            dispatch({
                meta: { actionTime: Date.now() },
                payload: catalogId,
                type: LOAD_BID_LIMIT_REQUEST,
            });
            const response = await api.getBidLimitBalance({ authToken, bidderId, catalogId, deployment });
            dispatch({
                meta: { actionTime: Date.now(), catalogId },
                payload: response.payload,
                type: LOAD_BID_LIMIT_SUCCESS,
            });
        } catch (error) {
            dispatch({
                error: true,
                meta: { catalogId },
                payload: error,
                type: LOAD_BID_LIMIT_FAIL,
            });
        }
    } else {
        return Promise.resolve();
    }
};
